// supabase-writes-via-cloudflare.js
// Browser → Shopify App Proxy (/apps/ok/*) → Cloudflare Worker → Supabase

const APP_PROXY_BASE = '/apps/ok';

const SYNC_SESSION_KEY = 'ok_sync_session_v1';
const SYNC_TTL_MS = 24 * 60 * 60 * 1000;

const SYNC_FAILURE_COOLDOWN_MS = 30 * 1000;

const GOKWIK_TOKEN_KEYS = ['KWIKSESSIONTOKEN', 'KWIKUSERTOKEN', 'kp-request-id', 'kp_user_id'];
const ANONYMOUS_CLAIM_TOKEN_KEY = 'ok_anonymous_health_claim_token';

// ---------------------------------------------------------------------------
// Error logging to sync_error_logs via Cloudflare Worker
// ---------------------------------------------------------------------------

async function _logSyncError(errorType, errorMessage, errorDetails = {}) {
  try {
    await fetch(`${APP_PROXY_BASE}/log-error`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        error_type: errorType,
        error_message: errorMessage,
        error_details: errorDetails,
        user_agent: navigator.userAgent,
        page_url: window.location.href
      })
    });
  } catch (e) {
    console.warn('[supabase-writes] Failed to log error via Worker:', e.message);
  }
}

let syncInFlight = false;
let syncDone = false;
let lastSyncFailureAt = 0;
let lastKnownKpUserId = null;
let lastKnownSessionToken = null;

// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------

document.addEventListener('DOMContentLoaded', () => {
  console.log('[supabase-writes] DOMContentLoaded — patch installed.');
  _patchLocalStorageSetItem();
});

// Expose sync function globally — called only by gokwik.liquid after login
window.__syncGoKwikUser = function (reason) {
  reason = reason || 'external';
  console.log(`[supabase-writes] __syncGoKwikUser called via ${reason}.`);
  syncWhenTokensReady(reason);
};

// ---------------------------------------------------------------------------
// Token detection via localStorage monkey-patch
// ---------------------------------------------------------------------------

function _patchLocalStorageSetItem() {
  const original = Storage.prototype.setItem;

  Storage.prototype.setItem = function (key, value) {
    const oldValue = this.getItem(key);
    original.call(this, key, value);

    if (GOKWIK_TOKEN_KEYS.includes(key)) {
      const changed = String(oldValue) !== String(value);
      console.log(`[supabase-writes] setItem(${key}) — changed: ${changed}, oldLen: ${oldValue?.length ?? 0}, newLen: ${value?.length ?? 0}`);

      if (changed && key === 'KWIKSESSIONTOKEN') {
        console.log('[supabase-writes] KWIKSESSIONTOKEN changed — clearing cache immediately.');
        console.log('[supabase-writes] Clearing localStorage keys:', {
          SYNC_SESSION_KEY,
          supabase_persistent_user_id: localStorage.getItem('supabase_persistent_user_id'),
          user_phone_number: localStorage.getItem('user_phone_number'),
          'kp-to-supabase-data-transfer': localStorage.getItem('kp-to-supabase-data-transfer')
        });
        localStorage.removeItem(SYNC_SESSION_KEY);
        localStorage.removeItem('supabase_persistent_user_id');
        localStorage.removeItem('user_phone_number');
        localStorage.removeItem('kp-to-supabase-data-transfer');
        syncDone = false;
        console.log('[supabase-writes] Cache cleared. syncDone set to false.');
      }
    }
  };

  console.log('[supabase-writes] localStorage.setItem patched for GoKwik token detection.');
}

// ---------------------------------------------------------------------------
// Unified sync entry point
// ---------------------------------------------------------------------------

async function syncWhenTokensReady(reason) {
  const tokens = _getGoKwikTokens();

  if (!tokens) {
    console.log(`[supabase-writes] Tokens not ready during ${reason}.`);
    return 'no_tokens';
  }

  console.log(`[supabase-writes] Tokens present during ${reason}:`, {
    kpUserId: tokens.kpUserId,
    sessionToken: tokens.checkoutAccessToken.slice(0, 20) + '...',
    gkAccessToken: tokens.gkAccessToken.slice(0, 20) + '...',
    kpRequestId: tokens.kpRequestId.slice(0, 20) + '...'
  });

  const cached = getSyncSession();

  if (isSyncSessionValid(cached, tokens)) {
    console.log('[supabase-writes] Using cached sync session:', {
      userId: cached.userId,
      phone: (cached.phone || '').slice(-4),
      kpUserId: cached.kpUserId,
      syncedAt: new Date(cached.syncedAt).toISOString()
    });
    restoreLegacySessionKeys(cached);
    lastKnownKpUserId = tokens.kpUserId;
    lastKnownSessionToken = tokens.checkoutAccessToken;
    return 'cached';
  }

  if (syncInFlight) {
    console.log(`[supabase-writes] Sync already in flight — skipping ${reason}.`);
    return 'in_flight';
  }

  const now = Date.now();

  if (lastSyncFailureAt && now - lastSyncFailureAt < SYNC_FAILURE_COOLDOWN_MS) {
    const remainingSec = Math.ceil((SYNC_FAILURE_COOLDOWN_MS - (now - lastSyncFailureAt)) / 1000);
    console.log(`[supabase-writes] Sync failed recently; cooldown active (${remainingSec}s remaining).`);
    return 'cooldown';
  }

  syncInFlight = true;

  try {
    const userId = await handleUserSync({ force: true });

    if (userId) {
      syncDone = true;
      lastKnownKpUserId = tokens.kpUserId;
      lastKnownSessionToken = tokens.checkoutAccessToken;
      console.log(`[supabase-writes] Sync SUCCESS via ${reason}. User ID: ${userId}`);
      return 'synced';
    }

    lastSyncFailureAt = Date.now();
    console.warn(`[supabase-writes] Sync FAILED via ${reason}.`);
    _logSyncError('SYNC_FAILED', `Sync failed via ${reason} — no userId returned`, {
      reason,
      tokens: {
        kpUserId: tokens.kpUserId,
        sessionToken: tokens.checkoutAccessToken.slice(0, 20) + '...',
        gkAccessToken: tokens.gkAccessToken.slice(0, 20) + '...',
        kpRequestId: tokens.kpRequestId.slice(0, 20) + '...'
      }
    });
    return 'failed';
  } catch (err) {
    lastSyncFailureAt = Date.now();
    console.error(`[supabase-writes] Sync FAILED with error during ${reason}:`, err.message);
    _logSyncError('SYNC_EXCEPTION', `Sync exception during ${reason}: ${err.message}`, {
      reason,
      error_name: err.name,
      tokens: {
        kpUserId: tokens.kpUserId,
        sessionToken: tokens.checkoutAccessToken.slice(0, 20) + '...',
        gkAccessToken: tokens.gkAccessToken.slice(0, 20) + '...',
        kpRequestId: tokens.kpRequestId.slice(0, 20) + '...'
      }
    });
    return 'failed';
  } finally {
    syncInFlight = false;
  }
}

// ---------------------------------------------------------------------------
// Sync session cache
// ---------------------------------------------------------------------------

function getSyncSession() {
  try {
    return JSON.parse(localStorage.getItem(SYNC_SESSION_KEY) || 'null');
  } catch {
    return null;
  }
}

function isSyncSessionValid(session, tokens) {
  if (!session) return false;
  if (!session.userId || !session.phone || !session.kpUserId || !session.syncedAt) return false;

  const syncedAt = Number(session.syncedAt);

  if (!Number.isFinite(syncedAt)) return false;
  if (Date.now() - syncedAt > SYNC_TTL_MS) return false;

  if (session.kpUserId !== tokens.kpUserId) return false;

  return true;
}

function saveSyncSession({ userId, phone, kpUserId }) {
  if (!userId || !phone || !kpUserId) {
    console.warn('[supabase-writes] Not saving incomplete sync session.', {
      hasUserId: !!userId,
      hasPhone: !!phone,
      hasKpUserId: !!kpUserId
    });
    return;
  }

  const session = {
    userId,
    phone,
    kpUserId,
    syncedAt: Date.now()
  };

  console.log('[supabase-writes] Saving sync session to localStorage:', {
    userId,
    phone: (phone || '').slice(-4),
    kpUserId,
    syncedAt: new Date(session.syncedAt).toISOString()
  });

  localStorage.setItem(SYNC_SESSION_KEY, JSON.stringify(session));

  localStorage.setItem('supabase_persistent_user_id', userId);
  localStorage.setItem('user_phone_number', phone);
  localStorage.setItem('kp-to-supabase-data-transfer', 'true');

  console.log('[supabase-writes] Legacy session keys restored:', {
    supabase_persistent_user_id: userId,
    user_phone_number: (phone || '').slice(-4),
    'kp-to-supabase-data-transfer': 'true'
  });
}

function restoreLegacySessionKeys(session) {
  console.log('[supabase-writes] Restoring legacy session keys from cache:', {
    userId: session.userId,
    phone: (session.phone || '').slice(-4),
    kpUserId: session.kpUserId
  });
  localStorage.setItem('supabase_persistent_user_id', session.userId);
  localStorage.setItem('user_phone_number', session.phone);
  localStorage.setItem('kp-to-supabase-data-transfer', 'true');
  console.log('[supabase-writes] Legacy keys set:', {
    supabase_persistent_user_id: session.userId,
    user_phone_number: (session.phone || '').slice(-4)
  });
}

// ---------------------------------------------------------------------------
// Core sync
// ---------------------------------------------------------------------------

async function handleUserSync({ force = false } = {}) {
  const tokens = _getGoKwikTokens();

  if (!tokens) {
    console.log('[supabase-writes] GoKwik tokens not present — user is anonymous, skipping sync.');
    return null;
  }

  const cached = getSyncSession();

  if (!force && isSyncSessionValid(cached, tokens)) {
    console.log('[supabase-writes] Using cached sync session:', {
      userId: cached.userId,
      phone: (cached.phone || '').slice(-4),
      kpUserId: cached.kpUserId
    });
    restoreLegacySessionKeys(cached);
    return cached.userId;
  }

  console.log('[supabase-writes] Calling Worker /sync-user with tokens:', {
    kpUserId: tokens.kpUserId,
    sessionToken: tokens.checkoutAccessToken.slice(0, 20) + '...',
    gkAccessToken: tokens.gkAccessToken.slice(0, 20) + '...',
    kpRequestId: tokens.kpRequestId.slice(0, 20) + '...'
  });

  const anonymousClaimToken = localStorage.getItem(ANONYMOUS_CLAIM_TOKEN_KEY);
  const payload = anonymousClaimToken ? { ...tokens, anonymousClaimToken } : tokens;

  const result = await _workerPost('/sync-user', payload);

  const userId = result?.data?.user_id ?? null;
  const phone = result?.data?.phone ?? null;
  const kpUserId = tokens.kpUserId;

  if (!userId || !phone) {
    const payload = {
      data: result?.data ? { user_id: result.data.user_id, phone: (result.data.phone || '').slice(-4) } : null,
      error: result?.error,
      tokens: {
        kpUserId: tokens.kpUserId,
        sessionToken: tokens.checkoutAccessToken.slice(0, 20) + '...'
      }
    };
    console.error('[supabase-writes] sync-user response missing user_id or phone:', payload);
    _logSyncError('SYNC_RESPONSE_MISSING_DATA', 'Worker returned sync-user response with missing user_id or phone', {
      ...payload,
      error_kind: result?.error_kind || null
    });
    return null;
  }

  saveSyncSession({ userId, phone, kpUserId });

  if (result?.data?.linked_anonymous) {
    localStorage.removeItem(ANONYMOUS_CLAIM_TOKEN_KEY);
    console.log('[supabase-writes] Anonymous health data linked and claim token cleared.', {
      linkedCount: result?.data?.linked_count || 0
    });
  }

  console.log('[supabase-writes] User synced SUCCESSFULLY:', {
    userId,
    phone: (phone || '').slice(-4),
    kpUserId,
    sessionToken: tokens.checkoutAccessToken.slice(0, 20) + '...'
  });

  return userId;
}

// ---------------------------------------------------------------------------
// Anonymous health data uses a claim token so it can be linked after GoKwik login.
// ---------------------------------------------------------------------------

async function submitAnonymousHealthData(heightFt, heightIn, weightKg, age, healthConditions) {
  const anonymousClaimToken = getOrCreateAnonymousClaimToken();
  console.log('[supabase-writes] Submitting anonymous health data via Worker.');

  const result = await _workerPost('/submit-health-anonymous', {
    anonymousClaimToken,
    heightFt: Number(heightFt),
    heightIn: Number(heightIn),
    weightKg: Number(weightKg),
    age: Number(age),
    healthConditions: Array.isArray(healthConditions) ? healthConditions : []
  });

  console.log('[supabase-writes] Anonymous health data stored.', result?.data || {});
  return result?.data;
}

// ---------------------------------------------------------------------------
// insertHealthData — public, called by bmi-calculator.liquid
// ---------------------------------------------------------------------------

async function insertHealthData(heightFt, heightIn, weightKg, age, healthConditions = []) {
  console.log('[supabase-writes] Submitting health data via Worker...', {
    heightFt,
    heightIn,
    weightKg,
    age,
    healthConditionsCount: healthConditions.length
  });

  let tokens = null;

  try {
    tokens = _getGoKwikTokens();

    if (!tokens) {
      console.log('[supabase-writes] GoKwik tokens missing — submitting health data anonymously.');
      return await submitAnonymousHealthData(heightFt, heightIn, weightKg, age, healthConditions);
    }

    console.log('[supabase-writes] Health data tokens:', {
      kpUserId: tokens.kpUserId,
      sessionToken: tokens.checkoutAccessToken.slice(0, 20) + '...'
    });

    const result = await _workerPost('/submit-health', {
      ...tokens,
      heightFt: Number(heightFt),
      heightIn: Number(heightIn),
      weightKg: Number(weightKg),
      age: Number(age),
      healthConditions: Array.isArray(healthConditions) ? healthConditions : []
    });

    console.log('[supabase-writes] Health data stored SUCCESSFULLY:', {
      ...result?.data,
      phone: (result?.data?.phone || '').slice(-4)
    });

    const userId = result?.data?.user_id ?? null;
    const phone = result?.data?.phone ?? null;

    if (userId && phone) {
      saveSyncSession({ userId, phone, kpUserId: tokens.kpUserId });
      syncDone = true;
    }

    return result?.data;
  } catch (err) {
    console.error('[supabase-writes] insertHealthData FAILED:', err.message);
    _logSyncError('HEALTH_DATA_SUBMIT_FAILED', `Health data submission failed: ${err.message}`, {
      error_name: err.name,
      tokens: tokens ? {
        kpUserId: tokens.kpUserId,
        sessionToken: tokens.checkoutAccessToken.slice(0, 20) + '...'
      } : null
    });
  }
}

window.insertHealthData = insertHealthData;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function _workerPost(path, payload) {
  const res = await fetch(`${APP_PROXY_BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload)
  });

  const text = await res.text();

  let data;

  try {
    data = JSON.parse(text);
  } catch {
    throw new Error(
      `[supabase-writes] Worker ${path} returned non-JSON (HTTP ${res.status}): ${text.slice(0, 200)}`
    );
  }

  if (!data?.ok) {
    const errorMsg = data?.error ?? `HTTP ${res.status}`;
    _logSyncError('WORKER_ERROR', `Worker ${path} error: ${errorMsg}`, {
      path,
      http_status: res.status,
      worker_error: data?.error,
      error_kind: data?.error_kind || null,
      response_body: text.slice(0, 500)
    });
    throw new Error(`[supabase-writes] Worker ${path} error: ${errorMsg}`);
  }

  return data;
}

function _getGoKwikTokens() {
  const checkoutAccessToken = localStorage.getItem('KWIKSESSIONTOKEN');
  const gkAccessToken = localStorage.getItem('KWIKUSERTOKEN');
  const kpRequestId = localStorage.getItem('kp-request-id');

  // kp_user_id is device-bound — same for all users on same device.
  // The unique constraint on kp_user_id in Supabase was removed so
  // multiple users can share the same device without 409 errors.
  let kpUserId = localStorage.getItem('kp_user_id');
  if (!kpUserId) {
    try {
      const usrTrck = JSON.parse(localStorage.getItem('usr_trck') || 'null');
      if (usrTrck && usrTrck.id) {
        kpUserId = usrTrck.id;
      }
    } catch (e) {}
  }

  if (!checkoutAccessToken || !gkAccessToken || !kpRequestId || !kpUserId) {
    return null;
  }

  return { checkoutAccessToken, gkAccessToken, kpRequestId, kpUserId };
}

function getOrCreateAnonymousClaimToken() {
  const existing = localStorage.getItem(ANONYMOUS_CLAIM_TOKEN_KEY);
  if (existing) return existing;

  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  const token = btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');

  localStorage.setItem(ANONYMOUS_CLAIM_TOKEN_KEY, token);
  return token;
}
